import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, computed, effect, inject, signal, untracked } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { type Observable, type Subscription, finalize } from 'rxjs';

import { PROFILE_COPY } from '../../features/profile/profile.copy';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { TokenService } from './token.service';
import { ToastService } from './toast.service';

/** Cuenta y token con los que se cargaron los ids. Cada carga crea uno nuevo: se compara por identidad. */
interface WishlistOwner {
  readonly id: number | null;
  readonly token: string | null;
}

const NO_OWNER: WishlistOwner = { id: null, token: null };
const NO_IDS: ReadonlySet<number> = new Set<number>();

/**
 * Corazones de la lista de deseos (W2 de B10, B28; contrato A1 en `docs/api/profile-api.md`): qué productos tiene
 * guardados la cuenta de la sesión y el alta o la baja de uno (`PUT`/`DELETE /profile/wishlist/{id}`).
 *
 * **Atado a la cuenta, no a `isAuthenticated`**: un login A → B directo deja `isAuthenticated` en `true`. Al cambiar
 * `currentUser()?.id` (incluido `null`) se cancelan las peticiones en vuelo, se vacía el set y, si hay cuenta, se pide
 * `GET /profile/wishlist/ids`. Cada callback comprueba de forma síncrona que su dueño sigue siendo el de ahora: una
 * respuesta de la cuenta anterior nunca escribe, ni revierte, ni saca un toast.
 *
 * **El dueño es `{id, token}`** (lección H1 de W3): `toggle()` no pide nada si la cuenta o el token de la sesión ya no
 * son los de la carga. Con el token cambiado (otra pestaña entró), lanza una resincronización con `auth.me()` y, con el
 * usuario EMITIDO (H1-p2: nunca `currentUser`), recarga los ids solo si es la misma cuenta; otra cuenta la recarga el
 * efecto.
 *
 * **Optimista con vuelta atrás**: `toggle()` cambia el set, lanza la petición y, ante error, revierte (si el dueño
 * sigue siendo el mismo) con el toast del caso. Un segundo toque del mismo producto en vuelo se ignora (candado).
 *
 * **SSR**: ni efecto, ni red, ni timers (`isPlatformBrowser` explícito, como H2 de W3); todo sale apagado.
 */
@Injectable({ providedIn: 'root' })
export class WishlistStore {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly tokens = inject(TokenService);
  private readonly toast = inject(ToastService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly copy = PROFILE_COPY.WISHLIST;

  private readonly _ids = signal<ReadonlySet<number>>(NO_IDS);
  private readonly _pending = signal<ReadonlySet<number>>(NO_IDS);
  private readonly _loaded = signal(false);

  /** Ids guardados de la cuenta de la sesión (vacío sin sesión, mientras carga y en SSR). */
  readonly ids = this._ids.asReadonly();
  /** `true` cuando `ids` ya es el de la cuenta de la sesión (llegó su `GET /ids`). */
  readonly loaded = this._loaded.asReadonly();

  private owner: WishlistOwner = NO_OWNER;
  private load: Subscription | null = null;
  /**
   * Productos tocados desde que salió el `/ids` en curso (AL-W2-3). Al llegar `/ids`, su estado actual manda sobre la
   * respuesta, aunque el toque ya haya terminado: un `/ids` que el servidor resolvió antes del toque lo pisaría.
   */
  private readonly touchedSinceLoad = new Set<number>();
  private readonly toggles = new Map<number, Subscription>();
  /** Resincronización tras ver otro token: una sola en vuelo. */
  private resync: Subscription | null = null;
  /** Recuperación de sesión tras un 401 (§5.6.7, como W1): una sola en vuelo. */
  private recovery: Subscription | null = null;

  constructor() {
    if (!this.isBrowser) return;
    const userId = computed(() => this.auth.currentUser()?.id ?? null);
    effect(() => {
      const id = userId();
      untracked(() => this.resetFor(id));
    });
  }

  isSaved(productId: number): boolean {
    return this._ids().has(productId);
  }

  /** El producto tiene un alta o una baja en vuelo (el botón se deshabilita). */
  pending(productId: number): boolean {
    return this._pending().has(productId);
  }

  /**
   * Guarda o quita `productId` para la cuenta de la sesión. Sin sesión no hace nada: el botón abre el login.
   */
  toggle(productId: number): void {
    if (!this.isBrowser || !Number.isSafeInteger(productId) || productId <= 0) return;
    const owner = this.owner;
    if (owner.id === null || owner.id !== this.auth.currentUser()?.id) return;
    if (this.pending(productId)) return;
    // TODO [D2] 1219118983256993: usar auth.sessionToken()
    if (owner.token !== this.tokens.getUserToken()) {
      this.resyncSession();
      return;
    }

    const wasSaved = this.isSaved(productId);
    this.write(productId, !wasSaved);
    this.setPending(productId, true);
    this.touchedSinceLoad.add(productId);

    const path = `/profile/wishlist/${productId}`;
    const request$: Observable<unknown> = wasSaved ? this.api.delete(path) : this.api.put(path, {});
    const sub = request$
      .pipe(
        finalize(() => {
          // Defensa ante un cambio de orden en `cancelAll()`; hoy equivalente (Alma, W2).
          if (this.owner !== owner) return;
          this.setPending(productId, false);
          this.toggles.delete(productId);
        }),
      )
      .subscribe({
        next: () => {
          if (!this.isCurrent(owner)) return;
          this.toast.show({ type: 'success', message: wasSaved ? this.copy.REMOVED : this.copy.SAVED });
        },
        error: (err: unknown) => {
          if (!this.isCurrent(owner)) return;
          this.write(productId, wasSaved);
          // AL-W2-4: el toque no llegó al servidor; un `/ids` en curso vuelve a mandar sobre este producto.
          this.touchedSinceLoad.delete(productId);
          this.toggleFailed(err);
        },
      });
    if (!sub.closed) this.toggles.set(productId, sub);
  }

  // ── Atado a la cuenta ────────────────────────────────────────────

  /** ¿`owner` sigue siendo el dueño del set y la cuenta de la sesión? */
  private isCurrent(owner: WishlistOwner): boolean {
    return this.owner === owner && owner.id !== null && this.auth.currentUser()?.id === owner.id;
  }

  /** Cambio de cuenta (o recarga de la misma con otro token): todo lo anterior se cancela y se olvida. */
  private resetFor(id: number | null): void {
    this.cancelAll();
    this._ids.set(NO_IDS);
    this._pending.set(NO_IDS);
    this._loaded.set(false);
    this.touchedSinceLoad.clear();
    // TODO [D2] 1219118983256993: usar auth.sessionToken()
    this.owner = id === null ? NO_OWNER : { id, token: this.tokens.getUserToken() };
    if (id !== null) this.loadIds(this.owner);
  }

  private loadIds(owner: WishlistOwner): void {
    this.load = this.api.get<{ data: number[] }>('/profile/wishlist/ids').subscribe({
      next: (res) => {
        if (!this.isCurrent(owner)) return;
        const ids = new Set((res.data ?? []).map(Number).filter(Number.isSafeInteger));
        // Un toque desde que salió esta petición manda (en vuelo o ya resuelto): su producto conserva el estado actual.
        const current = this._ids();
        for (const id of this.touchedSinceLoad) {
          if (current.has(id)) ids.add(id);
          else ids.delete(id);
        }
        this.touchedSinceLoad.clear();
        this._ids.set(ids);
        this._loaded.set(true);
      },
      error: (err: unknown) => {
        if (!this.isCurrent(owner)) return;
        // Sin copy para este fallo: los corazones quedan vacíos hasta la próxima carga.
        if (err instanceof HttpErrorResponse && err.status === 401) this.recoverSession();
      },
    });
  }

  /**
   * El token ya no es el de la carga (otra pestaña cambió la sesión). `me()` carga la cuenta del token nuevo (si es
   * otra, el efecto recarga) o limpia la sesión. Si vuelve la MISMA cuenta el id no cambia y el efecto no corre: se
   * recarga aquí, con el token nuevo, solo con la respuesta VIGENTE: el usuario emitido es el dueño, la sesión sigue
   * en él y el token no ha vuelto a cambiar desde que se pidió (`me()` emite `null` cuando descarta o se cancela).
   */
  private resyncSession(): void {
    if (this.resync && !this.resync.closed) return;
    const owner = this.owner;
    // TODO [D2] 1219118983256993: usar auth.sessionToken()
    const token = this.tokens.getUserToken();
    this.resync = this.auth.me().subscribe((user) => {
      const sameAccount =
        user !== null &&
        user.id === owner.id &&
        this.isCurrent(owner) &&
        // TODO [D2] 1219118983256993: usar auth.sessionToken()
        this.tokens.getUserToken() === token;
      if (sameAccount) this.resetFor(user.id);
    });
  }

  /** §5.6.7: `auth.me()`, que ante error limpia la sesión; nunca `logout()`, que navega a `/`. */
  private recoverSession(): void {
    if (this.recovery && !this.recovery.closed) return;
    this.recovery = this.auth.me().subscribe();
  }

  /** 422 → el texto del servidor; 401 → recuperar la sesión, sin toast (como W1); el resto → `TOGGLE_ERROR`. */
  private toggleFailed(err: unknown): void {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    if (status === 401) {
      this.recoverSession();
      return;
    }
    const message = status === 422 ? serverMessage(err as HttpErrorResponse) : null;
    this.toast.show({ type: 'error', message: message ?? this.copy.TOGGLE_ERROR });
  }

  private write(productId: number, saved: boolean): void {
    this._ids.update((ids) => withMember(ids, productId, saved));
  }

  private setPending(productId: number, pending: boolean): void {
    this._pending.update((ids) => withMember(ids, productId, pending));
  }

  private cancelAll(): void {
    this.load?.unsubscribe();
    this.load = null;
    this.resync?.unsubscribe();
    this.resync = null;
    // Tras vaciar el mapa: el `finalize` de cada baja ya no es del dueño nuevo y no toca nada.
    const toggles = [...this.toggles.values()];
    this.toggles.clear();
    this.owner = NO_OWNER;
    toggles.forEach((sub) => sub.unsubscribe());
  }
}

/** Texto del 422 de A1 (`{error: {product: [texto]}}`), o `null` si no lo trae. */
function serverMessage(err: HttpErrorResponse): string | null {
  const text = (err.error as { error?: { product?: unknown } } | null)?.error?.product;
  const first = Array.isArray(text) ? text[0] : null;
  return typeof first === 'string' && first ? first : null;
}

function withMember(set: ReadonlySet<number>, id: number, member: boolean): ReadonlySet<number> {
  if (set.has(id) === member) return set;
  const next = new Set(set);
  if (member) next.add(id);
  else next.delete(id);
  return next;
}
